import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import type { BOQSummary } from '../services/boqCalculationService';

interface BOQDisplayProps {
  boq: BOQSummary;
  systemSizeKw?: number;
}

export function BOQDisplay({ boq, systemSizeKw }: BOQDisplayProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  const weightDenominator = boq.subtotal > 0 ? boq.subtotal : 0;
  const categoryWeight = (amount: number) =>
    weightDenominator > 0 ? (amount / weightDenominator) * 100 : 0;

  const inferredSystemSizeKw = boq.items.find(
    (item) => item.category === 'Mounting Structure' && item.unit === 'kW' && item.quantity > 0
  )?.quantity;
  const effectiveSystemSizeKw =
    typeof systemSizeKw === 'number' && Number.isFinite(systemSizeKw) && systemSizeKw > 0
      ? systemSizeKw
      : inferredSystemSizeKw;
  const costPerKw =
    typeof effectiveSystemSizeKw === 'number' && Number.isFinite(effectiveSystemSizeKw) && effectiveSystemSizeKw > 0
      ? boq.grandTotal / effectiveSystemSizeKw
      : null;

  const preferredCategoryOrder = [
    'Solar Modules',
    'Inverters',
    'Mounting Structure',
    'DC Cables',
    'AC Cables',
    'Earthing Cable',
    'Earthing System',
    'Monitoring System',
    'Civil Works',
    'Miscellaneous',
    'Engineering & Design',
    'Project Management',
  ] as const;
  const categoryOrderIndex = new Map<string, number>(preferredCategoryOrder.map((c, i) => [c, i]));

  const sectionForCategory = (category: string): string => {
    if (category === 'Solar Modules' || category === 'Inverters') return 'Solar Modules & Inverters';
    if (['DC Cables', 'AC Cables', 'Earthing Cable', 'Earthing System'].includes(category)) return 'Cables & Earthing';
    if (['Monitoring System', 'Miscellaneous', 'Engineering & Design', 'Project Management'].includes(category)) {
      return 'Monitoring & Services';
    }
    return category || 'Other';
  };

  const sectionOrder = [
    'Solar Modules & Inverters',
    'Mounting Structure',
    'Cables & Earthing',
    'Monitoring & Services',
    'Civil Works',
  ] as const;
  const sectionOrderIndex = new Map<string, number>(sectionOrder.map((s, i) => [s, i]));

  const sectionsByName = boq.items.reduce((acc, item) => {
    const category = item.category || 'Other';
    const sectionName = sectionForCategory(category);
    const list = acc.get(sectionName) || [];
    list.push(item);
    acc.set(sectionName, list);
    return acc;
  }, new Map<string, BOQSummary['items']>());

  const sections = [...sectionsByName.entries()].sort(([a], [b]) => {
    const ai = sectionOrderIndex.get(a);
    const bi = sectionOrderIndex.get(b);
    if (ai != null && bi != null) return ai - bi;
    if (ai != null) return -1;
    if (bi != null) return 1;
    return a.localeCompare(b);
  });

  const sectionNames = sections.map(([name]) => name);
  const sectionKey = sectionNames.join('|');
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set(sectionNames));

  // When BOQ data changes (new calculation), open all sections by default.
  useEffect(() => {
    setOpenSections(new Set(sectionNames));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="h-6 w-6 text-blue-600" />
        <h3 className="text-xl font-bold text-gray-900">Bill of Quantities (BOQ)</h3>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-600 font-medium mb-1">Modules</p>
          <p className="text-lg font-bold text-blue-900">{formatCurrency(boq.subtotals.modules)}</p>
          <p className="text-xs text-blue-700 mt-1">{formatPercent(categoryWeight(boq.subtotals.modules))} of subtotal</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium mb-1">Inverters</p>
          <p className="text-lg font-bold text-green-900">{formatCurrency(boq.subtotals.inverters)}</p>
          <p className="text-xs text-green-700 mt-1">{formatPercent(categoryWeight(boq.subtotals.inverters))} of subtotal</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-sm text-purple-600 font-medium mb-1">Electrical</p>
          <p className="text-lg font-bold text-purple-900">{formatCurrency(boq.subtotals.electrical)}</p>
          <p className="text-xs text-purple-700 mt-1">{formatPercent(categoryWeight(boq.subtotals.electrical))} of subtotal</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <p className="text-sm text-orange-600 font-medium mb-1">Labor</p>
          <p className="text-lg font-bold text-orange-900">{formatCurrency(boq.subtotals.labor)}</p>
          <p className="text-xs text-orange-700 mt-1">{formatPercent(categoryWeight(boq.subtotals.labor))} of subtotal</p>
        </div>
      </div>

      {/* Categorized Items (Expandable Sections) */}
      <div className="mb-6">
        <div className="space-y-3">
          {sections.map(([sectionName, items]) => {
            const subtotal = items.reduce((sum, i) => sum + i.totalPrice, 0);
            const uniqueCategories = [...new Set(items.map((i) => i.category || 'Other'))].sort((a, b) => {
              const ai = categoryOrderIndex.get(a);
              const bi = categoryOrderIndex.get(b);
              if (ai != null && bi != null) return ai - bi;
              if (ai != null) return -1;
              if (bi != null) return 1;
              return a.localeCompare(b);
            });
            const isMultiCategorySection = uniqueCategories.length > 1;
            const isOpen = openSections.has(sectionName);
            return (
              <details
                key={sectionName}
                className="group bg-gray-50 border border-gray-200 rounded-lg"
                open={isOpen}
                onToggle={(e) => {
                  const open = e.currentTarget.open;
                  setOpenSections((prev) => {
                    const next = new Set(prev);
                    if (open) next.add(sectionName);
                    else next.delete(sectionName);
                    return next;
                  });
                }}
              >
                <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">{sectionName}</div>
                    <div className="text-xs text-gray-600">
                      {items.length} item{items.length !== 1 ? 's' : ''}
                      {isMultiCategorySection ? ` • ${uniqueCategories.join(', ')}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm font-semibold text-gray-900">{formatCurrency(subtotal)}</div>
                    <div className="text-gray-500 group-open:rotate-180 transition-transform">▾</div>
                  </div>
                </summary>
                <div className="border-t border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-white">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Item</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Specification</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Qty</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Unit</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Unit Price</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={`${sectionName}-${index}`} className="border-b border-gray-100 hover:bg-white/60">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">{item.description}</div>
                            {(() => {
                              const meta: string[] = [];
                              if (isMultiCategorySection && item.category) meta.push(item.category);
                              if (item.subcategory) meta.push(item.subcategory);
                              return meta.length > 0 ? (
                                <div className="text-xs text-gray-500 mt-1">{meta.join(' · ')}</div>
                              ) : null;
                            })()}
                          </td>
                          <td className="py-3 px-4 text-gray-600 text-xs max-w-xs">{item.specification}</td>
                          <td className="py-3 px-4 text-right text-gray-900">
                            {item.quantity.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                          <td className="py-3 px-4 text-right text-gray-600">{item.unit}</td>
                          <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(item.unitPrice)}</td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">{formatCurrency(item.totalPrice)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            );
          })}
        </div>
      </div>

      {/* Cost Summary */}
      <div className="mt-6 border-t border-gray-200 pt-6">
        <div className="space-y-3 max-w-md ml-auto">
          <div className="flex justify-between text-gray-700">
            <span>Material Cost:</span>
            <span className="font-medium">{formatCurrency(boq.totalMaterialCost)}</span>
          </div>
          <div className="flex justify-between text-gray-700">
            <span>Labor Cost:</span>
            <span className="font-medium">{formatCurrency(boq.totalLaborCost)}</span>
          </div>
          <div className="flex justify-between text-gray-700 pt-3 border-t border-gray-200">
            <span>Subtotal:</span>
            <span className="font-medium">{formatCurrency(boq.subtotal)}</span>
          </div>
          <div className="flex justify-between text-gray-700">
            <span>VAT (15%):</span>
            <span className="font-medium">{formatCurrency(boq.vat)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold text-gray-900 pt-3 border-t-2 border-gray-300">
            <span>Grand Total:</span>
            <span className="text-blue-600">{formatCurrency(boq.grandTotal)}</span>
          </div>
          <div className="flex justify-between text-gray-700">
            <span>Cost per kW:</span>
            <span className="font-medium">
              {costPerKw != null ? `${formatCurrency(costPerKw)}/kW` : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-900">
          <span className="font-semibold">Note:</span> This BOQ is based on current Saudi market rates (2024/2025) and includes all major components, labor, VAT, and professional fees. Actual costs may vary based on specific equipment selection, site conditions, and project complexity.
        </p>
      </div>
    </div>
  );
}
